import { useState, type FormEvent, type ReactElement } from 'react';
import { Link } from 'react-router';
import { messageForError } from '@proctoring/api-client';
import { useApi } from '../auth/AuthProvider.js';

/**
 * The copy shown after a successful submit. It is deliberately the same sentence whether
 * or not the address has an account — the API answers identically, and saying anything
 * more specific here would undo that on the client.
 */
const CONFIRMATION =
  'If that email address has an account, we have sent it a link to reset the password. The link expires in an hour.';

export function ForgotPasswordPage(): ReactElement {
  const api = useApi();

  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api.forgotPassword({ email });
      setSent(true);
    } catch (cause) {
      // Only a transport or validation failure reaches here; the API does not reject a
      // request for an address it does not know.
      setError(messageForError(cause));
    } finally {
      setSubmitting(false);
    }
  }

  if (sent) {
    return (
      <div className="flex h-full items-center justify-center px-6">
        <div className="w-full max-w-sm text-center">
          <h1 className="text-lg font-semibold text-session-text">Check your email</h1>
          <p className="mt-2 text-sm text-session-muted">{CONFIRMATION}</p>
          <Link to="/login" className="btn-secondary mt-5 inline-flex">
            Back to sign in
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full items-center justify-center px-6">
      <div className="w-full max-w-sm">
        <h1 className="text-lg font-semibold tracking-tight text-session-text">
          Reset your password
        </h1>
        <p className="mt-1 mb-6 text-sm text-session-muted">
          Enter the email address of your administrator account and we will send you a link to
          choose a new password.
        </p>

        <form onSubmit={onSubmit} className="panel space-y-4 p-5" noValidate>
          {error ? (
            <p role="alert" className="form-error">
              {error}
            </p>
          ) : null}

          <div>
            <label htmlFor="email" className="field-label">
              Email
            </label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              className="field-input"
            />
          </div>

          <button type="submit" disabled={submitting} className="btn-primary w-full">
            {submitting ? 'Sending…' : 'Send reset link'}
          </button>

          <p className="text-center text-sm">
            <Link to="/login" className="text-session-muted underline">
              Back to sign in
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
